import React, { useMemo, useState } from "react";
import { BookOpen, ChevronRight, ListTree, Scissors } from "lucide-react";

import { StatTile } from "@/shared/components/StatTile";
import { BODY_CAP, readPlan } from "./readPlan";

import type { KbNode } from "@/shared/types";

/* What the agent gets from this file, in the agent's own terms (readPlan mirrors
   `backend/modules/knowledge/tools/text.py`): the file whole, its sections one by one, or its
   first 40,000 characters. Then the numbers that go with it. */

const SEARCH_CAP = 250_000; // `content_tsv` indexes this much (migration 0050)
const SECTION_PREVIEW = 1_200;
const n = (value: number) => value.toLocaleString("en-US");

function Verdict({ icon: Icon, title, children }: { icon: typeof BookOpen; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift px-4 py-3.5 flex items-start gap-3">
      <span className="w-8 h-8 shrink-0 rounded-[10px] bg-[var(--surface-2)] flex items-center justify-center text-[var(--text-primary)]">
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </span>
      <div className="min-w-0">
        <div className="text-[13.5px] font-medium tracking-[-0.005em] text-[var(--text-primary)]">{title}</div>
        <p className="m-0 mt-0.5 text-[12.5px] leading-[1.5] text-[var(--text-tertiary)]">{children}</p>
      </div>
    </div>
  );
}

function Part({ index, title, chars, text }: { index: string; title: string; chars: number; text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="press w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[var(--accent-hover)]"
      >
        <span className="w-6 h-6 shrink-0 rounded-[7px] bg-[var(--surface-2)] flex items-center justify-center text-[11px] font-mono text-[var(--text-tertiary)]">{index}</span>
        <span className="flex-1 min-w-0 truncate text-[13px] text-[var(--text-primary)]">{title}</span>
        <span className="shrink-0 text-[11.5px] font-mono tabular-nums text-[var(--text-tertiary)]">{n(chars)}</span>
        <ChevronRight className="w-3.5 h-3.5 shrink-0 text-[var(--text-quaternary)]" strokeWidth={2} style={{ transform: `rotate(${open ? 90 : 0}deg)`, transition: "transform .18s var(--easing)" }} />
      </button>
      {open && (
        <p className="m-0 px-3 pb-3 pl-12 text-[12.5px] leading-[1.6] text-[var(--text-secondary)] whitespace-pre-wrap break-words fade-in">
          {text.length > SECTION_PREVIEW ? `${text.slice(0, SECTION_PREVIEW).trimEnd()}…` : text}
        </p>
      )}
    </div>
  );
}

export function KbIndexed({ node, text }: { node: KbNode; text: string }) {
  const plan = useMemo(() => readPlan(text), [text]);
  const summary =
    node.description_status === "ready" ? "Ready" : node.description_status === "failed" ? "None" : "Writing…";

  return (
    <div className="space-y-3">
      {plan.kind === "whole" ? (
        <Verdict icon={BookOpen} title="The agent reads this file whole">
          At {n(plan.chars)} characters it fits in one read (up to {n(BODY_CAP)}).
        </Verdict>
      ) : plan.kind === "sections" ? (
        <Verdict icon={ListTree} title="Too long to read whole: the agent reads it by section">
          It gets the opening and the list of {plan.sections.length} sections below, then opens the ones a task needs.
        </Verdict>
      ) : (
        <Verdict icon={Scissors} title={`The agent reads the first ${n(BODY_CAP)} characters`}>
          The last {n(plan.over)} characters are cut, and the agent is told so. Give the file ## headings and it reads it section by section instead.
        </Verdict>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 p-1 rounded-[18px] bg-[var(--surface-2)]">
        <StatTile label="Tokens" value={node.file?.tokens != null ? n(node.file.tokens) : "—"} sub={`${n(plan.chars)} characters`} />
        <StatTile label="Full-text search" value="On" sub={plan.chars > SEARCH_CAP ? `the first ${n(SEARCH_CAP)} characters` : "the whole text"} />
        <StatTile label="Summary" value={summary} sub="what the agent sees in listings" />
      </div>

      {plan.kind === "sections" && (
        <div className="rounded-[18px] bg-[var(--surface-2)] p-1">
          <div className="flex items-center justify-between min-h-[36px] px-3 pb-1 text-[12px] text-[var(--text-tertiary)]">
            <span>Sections the agent can open</span>
            <span>Characters</span>
          </div>
          <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] shadow-lift overflow-hidden divide-y divide-[var(--hairline)]">
            {plan.opening && <Part index="·" title="Opening (always sent)" chars={[...plan.opening].length} text={plan.opening} />}
            {plan.sections.map((s, i) => <Part key={`${i}-${s.title}`} index={String(i + 1)} title={s.title} chars={s.chars} text={s.text} />)}
          </div>
        </div>
      )}
    </div>
  );
}
